import styled from 'styled-components';
import { Box, Card, CardContent, Typography, Button } from '@mui/material';
import DefaultButton from 'components/Buttons/DefaultButton';

/* ---- Page shell ---- */

export const StyledContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: var(--background-info, #f5f7fa);
  overflow: auto;
`;

export const StyledHeader = styled(Box)`
  background-color: white;
  padding: 16px 12px;
  display: flex;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
  gap: 8px;

  .backButton {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.35rem 0.75rem;
    background: transparent;
    border: 1px solid #E0E0E0;
    border-radius: 8px;
    color: var(--primary-blue, #0B4598);
    font-family: var(--fontMain, Poppins), sans-serif;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    flex-shrink: 0;

    svg { font-size: 1.25rem; }

    &:hover { background: rgba(11, 69, 152, 0.06); border-color: var(--primary-blue, #0B4598); }
    &:active { background: rgba(11, 69, 152, 0.1); }
  }

  @media (min-width: 768px) { padding: 24px; gap: 16px; }
`;

export const StyledContent = styled(Box)`
  padding: 24px 16px;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;

  @media (min-width: 768px) { padding: 24px; }
`;

/* ---- Profile header card ---- */

export const ProfileCard = styled(Box)`
  background: var(--common-white-main, #fff);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
`;

export const ProfileRow = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
`;

export const ProfileInfo = styled(Box)`
  display: flex;
  align-items: center;
  gap: 16px;
`;

export const ProfileMeta = styled(Typography)`
  color: var(--text-secondary) !important;
  font-size: 0.875rem !important;
`;

export const StatusBadgeRow = styled(Box)`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

export const StatusPill = styled(Box)<{ $bg?: string; $color?: string; $borderColor?: string }>`
  background-color: ${p => p.$bg || '#0B4598'};
  color: ${p => p.$color || '#fff'};
  border: ${p => p.$borderColor ? `1.5px solid ${p.$borderColor}` : 'none'};
  padding: 4px 6px 4px 12px;
  border-radius: 9999px;
  font-weight: 600;
  font-size: 0.8rem;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

export const StatusInnerBadge = styled(Box)<{ $bg?: string; $color?: string }>`
  background-color: ${p => p.$bg || '#13CA82'};
  color: ${p => p.$color || '#fff'};
  padding: 2px 10px;
  border-radius: 9999px;
  font-weight: 700;
  font-size: 0.75rem;
  white-space: nowrap;
`;

export const StatusExplanationCard = styled(Box)`
  background: #fff;
  border-left: 4px solid var(--primary-blue, #0B4598);
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
  margin-bottom: 24px;
  padding: 14px 16px;
`;

/* ---- Five stat cards ---- */

export const StatCardsRow = styled(Box)`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;
  margin-bottom: 24px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (max-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

export const StatCard = styled(Card)`
  background-color: var(--common-white-main, #fff) !important;
  border-radius: 12px !important;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06) !important;
  padding: 16px !important;
  min-height: 120px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

export const StatCardContent = styled(CardContent)`
  padding: 0 !important;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  &:last-child { padding-bottom: 0 !important; }
`;

export const StatIconCircle = styled(Box)<{ $bg?: string }>`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background-color: ${p => p.$bg || '#EBF0F9'};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
  flex-shrink: 0;

  svg { font-size: 1.2rem; }
`;

export const StatTitleRow = styled(Box)`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  min-width: 0;
`;

export const StatCardTitleText = styled(Typography)`
  color: var(--text-secondary) !important;
  font-weight: 500 !important;
  font-size: 0.75rem !important;
  line-height: 1.3 !important;
`;

export const StatNumberText = styled(Typography)`
  color: var(--secondary-main, #111) !important;
  font-weight: 800 !important;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 1.5rem !important;
  @media (min-width: 600px) { font-size: 1.7rem !important; }
`;

export const StatSubnote = styled(Typography)`
  color: var(--text-secondary) !important;
  font-size: 0.7rem !important;
  margin-top: 4px !important;
`;

/* ---- Metrics grid: 2 columns on desktop ---- */

export const MetricsGrid = styled(Box)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const MetricsColumn = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

/* ---- Metric card (Reliability, Feedback, Affiliate, Profile) ---- */

export const MetricCard = styled(Box)`
  background: var(--common-white-main, #fff);
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
`;

export const MetricCardHeader = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;

export const MetricCardTitle = styled(Typography)`
  font-weight: 700 !important;
  font-size: 1rem !important;
  display: inline-flex !important;
  align-items: center !important;
  gap: 6px !important;
`;

export const PercentileBadge = styled(Box)<{ $bg?: string }>`
  background-color: ${p => p.$bg || '#13CA82'};
  color: #fff;
  padding: 4px 12px;
  border-radius: 9999px;
  font-weight: 600;
  font-size: 0.75rem;
  white-space: nowrap;
`;

export const MetricStatRow = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 8px 0;
`;

export const MetricStatLabel = styled(Typography)`
  color: var(--text-secondary) !important;
  font-size: 0.85rem !important;
`;

export const MetricStatValue = styled(Typography)`
  font-weight: 700 !important;
  font-size: 1.1rem !important;
  color: var(--secondary-main, #111) !important;
`;

export const IgnoredNote = styled(Box)`
  background-color: #f0faf5;
  border-radius: 8px;
  padding: 8px 12px;
  margin-top: 8px;
`;

/* ---- Feedback categories ---- */

export const FeedbackRow = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 0;
`;

export const FeedbackCategoryDot = styled(Box)`
  width: 6px;
  height: 6px;
  border-radius: 9999px;
  background-color: var(--primary-blue, #0B4598);
  flex-shrink: 0;
  margin-right: 10px;
`;

/* ---- Affiliate section ---- */

export const AffiliateCodeContainer = styled(MetricCard)``;

export const AffiliateCode = styled.code`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  color: var(--primary-green, #13CA82);
  background: #f8faf9;
  border: 1.5px solid #e0e5e2;
  border-radius: 10px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 1.5rem;
  font-weight: 700;
  letter-spacing: 2px;
  min-height: 80px;
  width: 100%;
  padding: 16px 12px;
  text-align: center;
  box-sizing: border-box;
  &:hover { background: #f0f5f2; }
`;

export const ShareButtonSmall = styled(Button)`
  background: var(--primary-blue, #0B4598) !important;
  color: #fff !important;
  border-radius: 8px !important;
  padding: 12px 24px !important;
  min-width: 140px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  margin: 0 !important;
`;

export const EmailCodeButton = styled(DefaultButton)`
  background: var(--primary-blue, #0B4598) !important;
  color: #fff !important;
  width: auto !important;
  min-width: 140px !important;
  padding: 12px 24px !important;
  margin: 0 !important;
  border-radius: 8px !important;
`;

/* ---- Professional Profile / Resume ---- */

export const ResumeButton = styled(Button)`
  text-transform: none !important;
  border-color: var(--primary-blue, #0B4598) !important;
  color: var(--primary-blue, #0B4598) !important;
  border-width: 1.5px !important;
  border-radius: 10px !important;
  font-weight: 600 !important;
  padding: 8px 20px !important;
  gap: 6px !important;
`;

/* ---- Provisional lock UI ---- */

export const LockedMetricsContainer = styled(Box)`
  border: 2px dashed rgba(0,0,0,0.15) !important;
  border-radius: 12px !important;
  background-color: #fff !important;
  padding: 32px !important;
  text-align: center !important;
  display: flex !important;
  flex-direction: column !important;
  align-items: center !important;
  margin-bottom: 24px !important;
`;

export const LockedIconCircle = styled(Box)`
  width: 56px;
  height: 56px;
  border-radius: 9999px;
  background: var(--divider, #E0E0E0) !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
`;

export const ProgressNarrowWrapper = styled(Box)`
  width: 50% !important;
  min-width: 180px !important;
  max-width: 320px !important;
  margin: 8px auto !important;
`;

/* ---- New member reward ---- */

export const RewardCard = styled(Box)<{ $bg?: string }>`
  background-color: ${p => p.$bg || '#0B4598'};
  border-radius: 16px;
  padding: 24px;
  color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin-bottom: 24px;
`;

export const RewardIconBox = styled(Box)`
  width: 56px;
  height: 56px;
  border-radius: 12px;
  background: rgba(229,231,235,0.2) !important;
  border: 1px solid rgba(255,255,255,0.25) !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
  font-size: 2.5rem;
`;

export const RewardProgressPanel = styled(Box)`
  background: rgba(229,231,235,0.2) !important;
  border: 1px solid rgba(255,255,255,0.25) !important;
  border-radius: 12px !important;
  padding: 12px !important;
  width: 100% !important;
  box-sizing: border-box !important;
  max-width: 560px !important;
  margin: 8px auto !important;
`;

export const RewardProgressTitle = styled(Typography)`
  color: #fff !important;
  margin-bottom: 6px !important;
  font-weight: 700 !important;
`;

export const RewardProgressTrack = styled(Box)`
  background: #335ea8;
  border-radius: 9999px;
  height: 14px;
  padding: 0 4px;
`;

export const RewardProgressFill = styled(Box)<{ $widthPct: number }>`
  background: var(--primary-green, #13CA82);
  border-radius: 9999px;
  height: 14px;
  width: ${p => Math.min(100, p.$widthPct)}%;
`;

export const RewardCTAWhite = styled(DefaultButton)`
  background: #fff !important;
  color: var(--primary-blue, #0B4598) !important;
  width: auto !important;
  padding: 8px 24px !important;
  border-radius: 9999px !important;
  min-width: 220px !important;
  &:hover { background: #f9fafb !important; }
`;

/* ---- Grandfather countdown ---- */

export const GrandfatherSection = styled(Box)`
  background: #EFF4F9;
  border-radius: 16px;
  padding: 24px;
  color: var(--secondary-main, #111);
  margin-bottom: 24px;
`;

export const GrandfatherTitleRow = styled(Box)`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 4px;
`;

export const GrandfatherClockIcon = styled(Box)`
  width: 40px;
  height: 40px;
  border-radius: 9999px;
  background: var(--background-info, #f5f7fa);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg { color: var(--primary-blue, #0B4598); font-size: 1.2rem; }
`;

export const GrandfatherProgressTrack = styled(Box)`
  background: #e5e7eb;
  border-radius: 9999px;
  height: 8px;
  width: 100%;
  margin: 12px 0 8px;
`;

export const GrandfatherProgressFill = styled(Box)<{ $widthPct: number }>`
  background: var(--primary-blue, #0B4598);
  border-radius: 9999px;
  height: 8px;
  width: ${p => Math.min(100, p.$widthPct)}%;
  transition: width 0.3s ease;
`;

export const GrandfatherMessage = styled(Box)`
  background: #EBF5FF;
  border-left: 4px solid var(--primary-blue, #0B4598);
  border-radius: 8px;
  padding: 12px 16px;
  margin-top: 16px;
`;

type MembershipBannerTone = 'info' | 'warning' | 'error';

const getMembershipBannerAccent = (tone: MembershipBannerTone) => {
  if (tone === 'error') return 'var(--error-main, #EB5757)';
  if (tone === 'warning') return 'var(--warning-main, #F59E0B)';
  return 'var(--primary-blue, #0B4598)';
};

const getMembershipBannerBorder = (tone: MembershipBannerTone) => {
  if (tone === 'error') return 'var(--error-light, #F5C2C7)';
  if (tone === 'warning') return 'var(--warning-light, #F0DFA0)';
  return 'var(--info-light, #B6DAFE)';
};

const getMembershipBannerTint = (tone: MembershipBannerTone) => {
  if (tone === 'error') return 'var(--error-background, #FEF3F3)';
  if (tone === 'warning') return 'var(--warning-background, #FFF8E1)';
  return 'var(--info-background, #E8F4FD)';
};

export const MembershipStatusBanner = styled(Box)<{ $tone: MembershipBannerTone }>`
  --membership-banner-accent: ${p => getMembershipBannerAccent(p.$tone)};
  --membership-banner-border: ${p => getMembershipBannerBorder(p.$tone)};
  --membership-banner-tint: ${p => getMembershipBannerTint(p.$tone)};

  background:
    linear-gradient(90deg, var(--membership-banner-tint) 0, rgba(255,255,255,0.92) 96px),
    var(--common-white-main, #fff);
  border: 1px solid var(--membership-banner-border);
  border-left: 4px solid var(--membership-banner-accent);
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(15,23,42,0.06);
  box-sizing: border-box;
  color: var(--text-primary, #111);
  margin-bottom: 24px;
  padding: 20px 22px;

  @media (max-width: 600px) {
    border-radius: 12px;
    padding: 16px;
  }
`;

export const MembershipBannerTitle = styled(Typography)`
  color: var(--text-primary, #111) !important;
  font-size: 1.05rem !important;
  font-weight: 700 !important;
  line-height: 1.35 !important;
  margin-bottom: 6px !important;
`;

export const MembershipBannerBody = styled(Typography)`
  color: var(--text-secondary, #516173) !important;
  line-height: 1.5 !important;
`;

export const MembershipRestrictionList = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 14px;
`;

export const MembershipRestrictionLine = styled(Typography)`
  align-items: flex-start;
  color: var(--text-primary, #111) !important;
  display: flex;
  font-weight: 600 !important;
  gap: 8px;
  line-height: 1.45 !important;

  &::before {
    background: var(--membership-banner-accent, var(--primary-blue, #0B4598));
    border-radius: 9999px;
    content: '';
    flex: 0 0 auto;
    height: 6px;
    margin-top: 8px;
    width: 6px;
  }
`;

export const MembershipCourseList = styled.ul`
  margin: 8px 0 0;
  padding-left: 18px;
`;

export const MembershipCourseListItem = styled.li`
  color: var(--text-primary, #111);
  font-size: 0.875rem;
  line-height: 1.43;
  margin-bottom: 4px;
`;

export const MembershipCourseCtaButton = styled(Button)`
  margin-top: 12px !important;
  border-radius: 9999px !important;
  padding: 8px 18px !important;
  text-transform: none !important;
  font-weight: 700 !important;
`;

/* ---- Misc / legacy compat ---- */

export const UnderlineLink = styled(Typography)`
  text-decoration: underline;
  cursor: pointer;
`;

export const JoinedCard = styled(Box)`
  background: #f7f7f7;
  border-radius: 12px;
  padding: 16px 24px;
  margin-bottom: 24px;
`;

export const JoinedRow = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

/* ---- Grandfathering retention guidance (T2-6060) ---- */

export type RetentionTagTone = 'green' | 'blue' | 'amber' | 'gray';

const retentionToneColors: Record<RetentionTagTone, { color: string; bg: string }> = {
  green: { color: '#07764D', bg: '#E8F9F1' },
  blue: { color: 'var(--primary-blue, #0B4598)', bg: '#EBF0F9' },
  amber: { color: '#B45309', bg: '#FFF3D6' },
  gray: { color: '#475569', bg: '#EEF2F7' },
};

export const RetentionGuidanceSection = styled(Box)`
  background: var(--common-white-main, #fff);
  border: 1px solid #E3EAF2;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
  padding: 20px 22px;
  margin-bottom: 24px;

  @media (max-width: 600px) {
    border-radius: 12px;
    padding: 16px;
  }
`;

export const RetentionGuidanceHeader = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 14px;

  @media (max-width: 700px) {
    flex-direction: column;
  }
`;

export const RetentionEyebrow = styled(Typography)`
  color: var(--primary-blue, #0B4598);
  font-size: 12px !important;
  font-weight: 800 !important;
  letter-spacing: 0;
  text-transform: uppercase;
  margin-bottom: 2px !important;
`;

export const RetentionStatusChips = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;

  @media (max-width: 700px) {
    justify-content: flex-start;
  }
`;

export const RetentionStatusChip = styled(Box)`
  border: 1px solid #DBE4EE;
  border-radius: 9999px;
  background: #F8FAFC;
  color: var(--text-secondary, #5f6b7a);
  font-size: 12px;
  font-weight: 600;
  padding: 5px 12px;
  white-space: nowrap;

  strong { color: var(--text-primary, #111); }
`;

export const RetentionPrimaryAction = styled(Box)`
  display: grid;
  grid-template-columns: 44px 1fr 36px;
  gap: 14px;
  align-items: center;
  background: #FFFDF6;
  border: 1px solid #F1C36D;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 14px;
`;

export const RetentionPrimaryIcon = styled(Box)`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  color: #B45309;
  background: #FFF3D6;

  svg { font-size: 1.3rem; }
`;

export const RetentionFactorRows = styled(Box)`
  border: 1px solid #E3EAF2;
  border-radius: 12px;
  overflow: hidden;
`;

export const RetentionFactorRow = styled(Box)`
  display: grid;
  grid-template-columns: 34px 1fr auto 34px;
  align-items: center;
  gap: 12px;
  padding: 13px 16px;
  border-bottom: 1px solid #EEF3F7;
  background: var(--common-white-main, #fff);

  &:last-child { border-bottom: 0; }

  @media (max-width: 700px) {
    grid-template-columns: 34px 1fr 34px;
  }
`;

export const RetentionFactorIcon = styled(Box)<{ $tone: RetentionTagTone }>`
  width: 34px;
  height: 34px;
  border-radius: 10px;
  display: grid;
  place-items: center;
  color: ${p => retentionToneColors[p.$tone].color};
  background: ${p => retentionToneColors[p.$tone].bg};

  svg { font-size: 1.1rem; }
`;

export const RetentionConfidenceTag = styled(Box)<{ $tone: RetentionTagTone }>`
  border-radius: 9999px;
  padding: 4px 9px;
  font-size: 11px;
  font-weight: 800;
  white-space: nowrap;
  color: ${p => retentionToneColors[p.$tone].color};
  background: ${p => retentionToneColors[p.$tone].bg};

  @media (max-width: 700px) {
    grid-column: 2;
    justify-self: flex-start;
  }
`;

export const RetentionInfoButton = styled('button')`
  width: 34px;
  height: 34px;
  border-radius: 9999px;
  border: 1px solid #CCD8E5;
  color: var(--primary-blue, #0B4598);
  background: var(--common-white-main, #fff);
  display: grid;
  place-items: center;
  padding: 0;
  cursor: pointer;

  svg { font-size: 1.05rem; }
`;

export const RetentionSheetScrim = styled(Box)`
  position: fixed;
  inset: 0;
  background: rgba(17, 24, 39, 0.34);
  z-index: 1300;
`;

export const RetentionDetailSheet = styled(Box)`
  position: fixed;
  left: 50%;
  bottom: 28px;
  transform: translateX(-50%);
  width: min(560px, calc(100vw - 32px));
  background: var(--common-white-main, #fff);
  border: 1px solid #E3EAF2;
  border-radius: 16px;
  box-shadow: 0 24px 60px rgba(17, 24, 39, 0.24);
  padding: 10px 22px 22px;
  z-index: 1301;

  @media (max-width: 600px) {
    bottom: 12px;
  }
`;

export const RetentionSheetHandle = styled(Box)`
  width: 44px;
  height: 4px;
  border-radius: 9999px;
  background: #D8E0E8;
  margin: 0 auto 16px;
`;

export const RetentionSheetHead = styled(Box)`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

export const RetentionSheetIcon = styled(Box)<{ $tone: RetentionTagTone }>`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  flex-shrink: 0;
  color: ${p => retentionToneColors[p.$tone].color};
  background: ${p => retentionToneColors[p.$tone].bg};
`;

export const RetentionSheetCallout = styled(Box)`
  background: #EBF0F9;
  border-left: 4px solid var(--primary-blue, #0B4598);
  border-radius: 8px;
  color: var(--primary-blue, #0B4598);
  padding: 12px 14px;
  font-size: 14px;
  line-height: 1.45;
  margin-bottom: 12px;

  strong {
    display: block;
    color: var(--text-primary, #111);
    margin-bottom: 2px;
  }
`;

export const RetentionSheetGrid = styled(Box)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 16px;

  & > div {
    border: 1px solid #E3EAF2;
    border-radius: 10px;
    padding: 10px;
  }

  span {
    display: block;
    color: var(--text-secondary, #5f6b7a);
    font-size: 11px;
    font-weight: 800;
    text-transform: uppercase;
    margin-bottom: 4px;
  }

  strong { font-size: 13px; }
`;

export const RetentionSheetButton = styled(Button)`
  width: 100%;
  border-radius: 10px !important;
  text-transform: none !important;
  font-weight: 700 !important;
`;

export const RetentionFooterNote = styled(Typography)`
  display: block !important;
  color: var(--text-secondary, #5f6b7a);
  margin-top: 10px !important;
`;
